import { Alert, ArrowLeftOutlined, Button, message, Radio } from 'gokwik-ui-kit'
import { useEffect, useState } from 'react'
import { PreOnboardingConfig } from './config'
import { getKeyForLabel, getPrevQuestionStatus, pushResponseToMerchantConfig } from '../utils'
import { useSelector } from 'react-redux'
import { getMerchantDetails, getUserData } from '@store/user/selectors'
import { makeAPICall } from '@gokwik/utilities'
import APIEndPoints from '@library/utilities/constants/apiEndpoints'
import { fetchUserDetails } from '@store/user'
import { useAppDispatch } from '@library/utilities/hooks'
import { additionalDocumentsByCategory, documentToQuestion } from '../constants'
import { logEvent } from '@library/utilities/userLogsEvents/userLogEvents'

const PreOnboardingJourney = ({ currentStep }) => {
    const [selected, setSelected] = useState<string | null>(null)
    const [currentIndex, setCurrentIndex] = useState(currentStep < 7 ? 0 : currentStep - 7)
    const [hasInventory, setHasInventory] = useState<boolean | null>(null)
    const [showInventoryError, setShowInventoryError] = useState(false)
    const currentQuestion = PreOnboardingConfig[currentIndex]
    const merchant_details = useSelector(getMerchantDetails)
    const dispatch = useAppDispatch()
    const userData = useSelector(getUserData)
    const [subSelections, setSubSelections] = useState<Record<string, boolean>>({
        'Do You Sell Edible Food Products?': false,
        'Do You Sell Ayurvedic Products?': false,
        'Do You Sell Gold and Silver Products?': false,
    })

    useEffect(() => {
        if (currentIndex === 0) {
            const storedEntityType = userData?.kycData?.entity_type?.toString()
            if (storedEntityType) {
                setSelected(storedEntityType)
            }
        } else if (currentIndex === 1) {
            const storedBusinessCat = userData?.kycData?.business_category?.toString()
            const hasInventory = userData?.kycData?.maintains_inventory
            const ayurvedic = userData?.kycData?.sell_ayurvedic_products
            const fssai = userData?.kycData?.sell_edible_food_products
            const gold_silver = userData?.kycData?.sell_gold_and_silver_products

            setSubSelections({
                'Do You Sell Edible Food Products?': fssai || false,
                'Do You Sell Ayurvedic Products?': ayurvedic || false,
                'Do You Sell Gold and Silver Products?': gold_silver || false,
            })

            if (typeof hasInventory === 'boolean') {
                if (hasInventory) {
                    setHasInventory(true)
                } else {
                    setHasInventory(false)
                }
            }

            if (storedBusinessCat?.toLowerCase() === 'ecommerce') {
                setSelected('Others')
            } else if (storedBusinessCat) {
                setSelected(storedBusinessCat)
            }
        } else if (currentIndex === 2) {
            const storedGstStatus = userData?.kycData?.gst_certificate?.toString()
            if (storedGstStatus) {
                setSelected(storedGstStatus)
            }
        }
    }, [currentIndex])

    const getQuestionOptions = () => {
        if (currentQuestion.key === 'business_category' && userData?.kycData?.allowed_business_categories) {
            return userData.kycData.allowed_business_categories.map((category) => ({
                key: category,
                value: category,
            }))
        }
        return currentQuestion?.options
    }

    const questionOptions = getQuestionOptions()

    const triggerEvents = (index, key, selectedValue, extraData = {}) => {
        if (index === 0) {
            logEvent(
                'kyc_business_type_selected',
                'click',
                'kyc',
                localStorage.getItem('email'),
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
                { [key]: selectedValue },
            )
        } else if (index === 1) {
            logEvent(
                'kyc_business_category_selected',
                'click',
                'kyc',
                localStorage.getItem('email'),
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
                { [key]: selectedValue, ...extraData },
            )
        } else if (index === 2) {
            logEvent(
                'kyc_gst_status_selected',
                'click',
                'kyc',
                localStorage.getItem('email'),
                userData?.merchantDetails?.m_id,
                userData?.merchantDetails?.short_name,
                userData?.userDetails?.name,
                { [key]: selectedValue },
            )
        }
    }

    const onNext = async () => {
        if (currentQuestion?.key === 'business_category' && hasInventory === false) {
            setShowInventoryError(true)
            return
        }
        sessionStorage.setItem(currentQuestion.key, selected)

        const subSelectionPayload = Object.entries(subSelections).reduce(
            (acc: Record<string, boolean>, [label, value]) => {
                const key = getKeyForLabel(label)
                if (key) {
                    acc[key] = value
                }
                return acc
            },
            {},
        )

        triggerEvents(currentIndex, currentQuestion.key, selected, subSelectionPayload)

        if (userData?.kycData?.entity_type === 10 && currentIndex === 1) {
            setCurrentIndex(2)
            return
        }

        const response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.updateMerchantOnboardingStatus + merchant_details?.m_id,
            payload: {
                [currentQuestion.key]: currentIndex === 0 ? parseInt(selected) : selected,
                current_onboarding_status: currentQuestion?.onboarding_step,
                ...(currentIndex !== 0 ? subSelectionPayload : {}),
                ...(currentIndex === 2 ? { business_category: sessionStorage.getItem('business_category') } : {}),
                ...(currentIndex !== 0 ? { maintains_inventory: selected === 'Others' ? hasInventory : true } : {}),
            },
            skipLoader: true,
        })

        if (response?.status === 200) {
            setSelected(null)
            dispatch(fetchUserDetails())
        }
    }

    const onBack = async () => {
        const status = getPrevQuestionStatus(PreOnboardingConfig[currentIndex - 1]?.onboarding_step)

        const response = await makeAPICall({
            method: 'post',
            url: process.env.REACT_APP_BASE_URL + APIEndPoints.updateMerchantOnboardingStatus + merchant_details?.m_id,
            payload: {
                current_onboarding_status: status,
            },
            skipLoader: true,
        })

        if (response?.status === 200) {
            dispatch(fetchUserDetails())
        }
    }

    const toggleSubSelection = (key: string) => {
        setSubSelections((prev) => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    const cleanUpStates = () => {
        setSubSelections({
            'Do You Sell Edible Food Products?': false,
            'Do You Sell Ayurvedic Products?': false,
            'Do You Sell Gold and Silver Products?': false,
        })
        setHasInventory(null)
    }

    return (
        <div className='flex flex-col max-w-lg m-auto mb-4'>
            <div className='flex flex-col mt-8 justify-start'>
                <p className='font-semibold'>
                    {currentIndex !== 0 && (
                        <span className='mr-3 cursor-pointer' onClick={onBack}>
                            <ArrowLeftOutlined />
                        </span>
                    )}
                    {currentQuestion?.title}
                </p>
                <div className='flex flex-col gap-2 w-full mt-3'>
                    <Radio.Group
                        onChange={(e) => {
                            cleanUpStates()
                            setSelected(e.target.value)
                        }}
                        value={selected}
                        className='w-full'
                    >
                        {questionOptions.map((option) => (
                            <div key={option.value}>
                                <div
                                    className={`flex items-center w-full px-4 py-3 mb-2 border rounded-xl cursor-pointer transition-all 
                                    ${
                                        selected === option.value
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-300 bg-white'
                                    }
                                    `}
                                    onClick={() => {
                                        cleanUpStates()
                                        setSelected(option.value)
                                    }}
                                >
                                    <Radio value={option.value} className='mr-2'>
                                        {option.key}
                                    </Radio>
                                </div>

                                {selected === option.value && currentQuestion?.key === 'business_category' && (
                                    <div className='mb-4 bg-gray-50 p-4 border border-gray-200 rounded-lg'>
                                        <div className='mb-4 bg-gray-50 p-4 border border-gray-200 rounded-lg pl-0'>
                                            <p className='text-black text-sm font-normal mb-3'>
                                                Do you manage your own inventory instead of drop-shipping?
                                            </p>
                                            <Radio.Group
                                                onChange={(e) => {
                                                    setHasInventory(e.target.value === 'yes')
                                                    setShowInventoryError(e.target.value !== 'yes')
                                                }}
                                                value={
                                                    hasInventory === true
                                                        ? 'yes'
                                                        : hasInventory === false
                                                        ? 'no'
                                                        : undefined
                                                }
                                            >
                                                <div className='flex gap-2'>
                                                    <Radio value='yes'>Yes</Radio>
                                                    <Radio value='no'>No</Radio>
                                                </div>
                                            </Radio.Group>
                                            {showInventoryError && (
                                                <Alert
                                                    message={"We don't support inventory-free  retail businesses."}
                                                    type='error'
                                                    showIcon
                                                    className='mt-2 text-sm'
                                                />
                                            )}
                                        </div>

                                        {additionalDocumentsByCategory[option.value] &&
                                            additionalDocumentsByCategory[option.value].map((document, index) => {
                                                const questionKey = documentToQuestion[document]
                                                if (!questionKey) return null

                                                return (
                                                    <div
                                                        key={index}
                                                        className='flex justify-between items-center py-2 border-b border-gray-200'
                                                    >
                                                        <span className='text-black text-sm font-normal'>
                                                            {questionKey}
                                                        </span>
                                                        <label className='relative inline-flex items-center cursor-pointer'>
                                                            <input
                                                                type='checkbox'
                                                                className='sr-only peer'
                                                                checked={subSelections[questionKey]}
                                                                onChange={() => toggleSubSelection(questionKey)}
                                                            />
                                                            <div
                                                                className={`w-11 h-6 rounded-full peer ${
                                                                    subSelections[questionKey]
                                                                        ? 'bg-[#004B8D] after:translate-x-full'
                                                                        : 'bg-gray-200'
                                                                } after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}
                                                            ></div>
                                                        </label>
                                                    </div>
                                                )
                                            })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </Radio.Group>
                </div>
                <Button
                    className='w-full mt-3 h-8 font-semibold mb-4'
                    variant='primary'
                    onClick={async () => await onNext()}
                    disabled={
                        !selected ||
                        (currentQuestion?.key === 'business_category' && hasInventory === null) ||
                        (currentQuestion?.key === 'business_category' && showInventoryError) ||
                        (currentQuestion?.key === 'gst_certificate' && !['Yes', 'No'].includes(selected))
                    }
                >
                    {currentQuestion?.ctaText}
                </Button>
            </div>
        </div>
    )
}

export default PreOnboardingJourney
